import {
  huge,
  hugeZero,
  isNonNegativeHuge,
  type HugeNumber,
  type HugeNumberInput,
} from "@/shared/numbers";

export interface ComputeTimOfflineRewardInput {
  readonly gameElapsedMs: number;
  readonly timGainPerSecond: HugeNumberInput;
  readonly timAllocation: number;
  readonly passiveResourceBalanceBase: number;
  readonly hiddenTimGainGrowthPerSecond: HugeNumberInput;
}

export function computeTimOfflineReward(
  input: ComputeTimOfflineRewardInput,
): HugeNumber {
  assertFiniteNonNegative(input.gameElapsedMs, "gameElapsedMs");
  assertFiniteNonNegative(input.timAllocation, "timAllocation");
  if (
    !Number.isFinite(input.passiveResourceBalanceBase) ||
    input.passiveResourceBalanceBase <= 0
  ) {
    throw new RangeError(
      "passiveResourceBalanceBase must be a positive finite number",
    );
  }

  const timGainPerSecond = requireFiniteNonNegativeHuge(
    input.timGainPerSecond,
    "timGainPerSecond",
  );
  const hiddenTimGainGrowthPerSecond = requireFiniteNonNegativeHuge(
    input.hiddenTimGainGrowthPerSecond,
    "hiddenTimGainGrowthPerSecond",
  );
  if (input.gameElapsedMs === 0 || input.timAllocation === 0) {
    return hugeZero();
  }

  const elapsedSeconds = input.gameElapsedMs / 1_000;
  const allocationScale =
    input.timAllocation / input.passiveResourceBalanceBase;
  return timGainPerSecond
    .mul(elapsedSeconds)
    .add(
      hiddenTimGainGrowthPerSecond.mul(0.5 * elapsedSeconds * elapsedSeconds),
    )
    .mul(allocationScale);
}

function assertFiniteNonNegative(value: number, name: string): void {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${name} must be a finite non-negative number`);
  }
}

function requireFiniteNonNegativeHuge(
  value: HugeNumberInput,
  name: string,
): HugeNumber {
  const normalized = huge(value);
  if (!normalized.isFinite() || !isNonNegativeHuge(normalized)) {
    throw new RangeError(`${name} must be finite and non-negative`);
  }
  return normalized;
}
